import * as React from 'react';
import { useState } from 'react';

import * as SliderPrimitive from '@radix-ui/react-slider';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { cn } from '@/lib/utils';

/**
 * Slider component built on Radix UI Slider primitive
 *
 * @param {Object} props - Component props
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} Slider component
 */
const Slider = ({
  className,
  tooltip,
  minLabel,
  maxLabel,
  displayRealValue = false,
  ref,
  ...props
}: React.ComponentProps<typeof SliderPrimitive.Root> & {
  tooltip?: boolean;
  minLabel?: string;
  maxLabel?: string;
  displayRealValue?: boolean;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  // Radix focuses the thumb on drag too, so focus also covers touch and keyboard
  const [isFocused, setIsFocused] = useState(false);

  const percentageValue =
    (((props.value[0] - props.min) / (props.max - props.min)) * 100).toFixed() + '%';
  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between">
        <span className="text-sm text-muted-foreground">{minLabel}</span>
        <span className="text-sm text-muted-foreground">{maxLabel}</span>
      </div>
      <SliderPrimitive.Root
        ref={ref}
        className={cn('relative flex w-full touch-none items-center select-none', className)}
        {...props}
      >
        <SliderPrimitive.Track className="relative h-2 w-full grow overflow-hidden rounded-full bg-muted">
          <SliderPrimitive.Range className="absolute h-full bg-selected" />
        </SliderPrimitive.Track>
        {tooltip && (
          <InfoTooltip
            content={String(displayRealValue ? props.value[0] : percentageValue)}
            open={isHovered || isFocused}
            size="auto"
          >
            <SliderPrimitive.Thumb
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              className="block h-5 w-5 rounded-full border-2 border-selected bg-background ring-offset-background transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
            />
          </InfoTooltip>
        )}
        {!tooltip && (
          <SliderPrimitive.Thumb className="block h-5 w-5 rounded-full border-2 border-selected bg-background ring-offset-background transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50" />
        )}
      </SliderPrimitive.Root>
    </div>
  );
};

export { Slider };
